import { useState } from 'react'
import { useLocation } from 'wouter'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FloatingInput } from '@/components/ui/floating-input'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'

const usernameSchema = z.object({
	username: z
		.string()
		.min(3, 'Username must be 3-20 characters')
		.max(20, 'Username must be 3-20 characters')
		.regex(
			/^(?=.{3,20}$)(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/,
			'Only letters, numbers, dots, and underscores. Cannot start/end with or have consecutive dots/underscores'
		),
})

type UsernameFormData = z.infer<typeof usernameSchema>

export default function ChangeUsernamePage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { uid } = useUser()
	const [isUpdating, setIsUpdating] = useState(false)

	const form = useForm<UsernameFormData>({
		resolver: zodResolver(usernameSchema),
		defaultValues: {
			username: ''
		}
	})

	const onSubmit = async (data: UsernameFormData) => {
		setIsUpdating(true)
		try {
			const response = await apiFetch(`/v1/users/${uid}`, {
				method: 'PATCH',
				body: JSON.stringify({
					username: data.username.trim(),
				}),
				withCredentials: true
			})

			const result = await response.json()

			if (!response.ok) {
				throw new Error(result.error?.message || 'Failed to update username')
			}

			toast({
				title: 'Username Updated',
				description: 'Your username has been updated successfully.'
			})
			navigate('/account')
		} catch (error) {
			toast({
				title: 'Update Failed',
				description: error instanceof Error ? error.message : 'Failed to update username',
				variant: 'destructive'
			})
		} finally {
			setIsUpdating(false)
		}
	}

	return (
		<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				{/* Header */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Change Username
					</h1>
				</div>

				{/* Center content */}
				<div className="flex-1 flex items-center justify-center px-5 pb-20">
					<div className="w-full max-w-sm space-y-5">
						<FloatingInput
							label="Username"
							type="text"
							autoFocus
							{...form.register('username')}
						/>
						{form.formState.errors.username && (
							<p className="text-sm text-[#EF4444] font-figtree font-normal leading-[16px]">
								{form.formState.errors.username.message}
							</p>
						)}

						<Button
							onClick={form.handleSubmit(onSubmit)}
							disabled={isUpdating}
							className="w-full h-[42px] bg-[#EF4444] rounded-[26px] text-white font-semibold font-figtree shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96]"
						>
							{isUpdating ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Updating...
								</>
							) : (
								'Update Username'
							)}
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}
